import { useState } from "react";
import { Columns3, Download, FileSpreadsheet, FileText, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { exportTable, withoutColumns, type ExportFormat, type ExportTable } from "@/lib/export";
import { loadHiddenColumns, saveHiddenColumns } from "@/lib/export-columns";

/**
 * One export control for every table in the console, so CSV/Excel/PDF behave
 * the same everywhere. `build` is called at click time rather than on render —
 * the export must reflect the filters showing right now, and building a table
 * on every re-render would be wasted work. It may be async: a paged table
 * fetches every matching row, not just the page on screen.
 *
 * With `columns`, the menu also lets the admin leave columns out
 * (FEAT-2026-029). `all` names every header `build` produces; the choice is
 * remembered on this computer under `storageKey`, one per page.
 */
export function ExportMenu({
  build,
  disabled,
  label = "Export",
  columns,
}: {
  build: () => ExportTable | null | Promise<ExportTable | null>;
  disabled?: boolean;
  label?: string;
  columns?: { storageKey: string; all: string[] };
}) {
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [hidden, setHidden] = useState<string[]>(() => (columns ? loadHiddenColumns(columns.storageKey) : []));
  const [choosing, setChoosing] = useState(false);

  // Only names that are still columns count, and a choice that would leave
  // nothing (an edited or stale saved value) exports everything instead.
  const all = columns?.all ?? [];
  const named = hidden.filter((h) => all.includes(h));
  const hiddenNow = named.length < all.length ? named : [];
  const shownCount = all.length - hiddenNow.length;

  const remember = (next: string[]) => {
    setHidden(next);
    if (columns) saveHiddenColumns(columns.storageKey, next);
  };
  const setShown = (name: string, show: boolean) =>
    remember(show ? hidden.filter((h) => h !== name) : [...hidden.filter((h) => h !== name), name]);

  const run = async (format: ExportFormat) => {
    setBusy(format);
    try {
      const table = await build();
      if (!table || table.rows.length === 0) {
        toast.error("Nothing to export", { description: "No rows match the current filters." });
        return;
      }
      await exportTable(columns ? withoutColumns(table, hiddenNow) : table, format);
    } catch (e) {
      toast.error("Export failed", {
        description: e instanceof Error ? e.message : "Please try again.",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" disabled={disabled || busy !== null}>
            {busy ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-3.5 w-3.5" />
            )}
            {label}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={columns ? "w-60" : "w-52"}>
          <DropdownMenuItem onSelect={() => run("xlsx")}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Excel (.xlsx)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => run("csv")}>
            <FileText className="mr-2 h-4 w-4" />
            CSV (.csv)
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => run("pdf")}>
            <Printer className="mr-2 h-4 w-4" />
            PDF
            <span className="ml-auto text-[10px] text-muted-foreground">via print</span>
          </DropdownMenuItem>
          {columns && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChoosing(true)}>
                <Columns3 className="mr-2 h-4 w-4" />
                Choose columns…
                <span className="ml-auto whitespace-nowrap pl-3 text-[10px] text-muted-foreground">
                  {hiddenNow.length ? `${shownCount} of ${all.length}` : "All"}
                </span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {columns && (
        <Dialog open={choosing} onOpenChange={setChoosing}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Columns to export</DialogTitle>
              <DialogDescription>
                Unticked columns are left out of Excel, CSV and PDF. Remembered on this computer.
              </DialogDescription>
            </DialogHeader>
            {/* Down the first column, then the second: the export's own order. */}
            <div
              className="grid grid-flow-col grid-cols-2 gap-x-4 gap-y-2 py-2"
              style={{ gridTemplateRows: `repeat(${Math.ceil(all.length / 2)}, auto)` }}
            >
              {all.map((name, i) => {
                const id = `export-column-${columns.storageKey}-${i}`;
                const shown = !hiddenNow.includes(name);
                return (
                  <div key={name} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={shown}
                      // One column always stays: an export of nothing is no export.
                      disabled={shown && shownCount === 1}
                      onCheckedChange={(v) => setShown(name, v === true)}
                    />
                    <Label htmlFor={id} className="font-normal">{name}</Label>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => remember([])} disabled={hiddenNow.length === 0}>
                Show all columns
              </Button>
              <Button onClick={() => setChoosing(false)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
